import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faClock, faCalendar, faSync } from '@fortawesome/free-solid-svg-icons';
import SftGlobalData from '../../library/SftGlobalData';

const QuizResultsLeaderboard = ({ 
  props, 
  points, 
  timer, 
  onRestart, 
  loading 
}) => {
  const [rankings, setRankings] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(true);
  const [currentUserRank, setCurrentUserRank] = useState(null);

  useEffect(() => {
    fetchRankings();
  }, []);

  const fetchRankings = async () => {
    try {
      setLoadingRankings(true);
      
      let postData = {key: props.api};
      
      const response = await SftGlobalData.post('quiz&action=rankings', postData, {
        headers: {'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'},
        timeout: 600000
      });

      if (response.data && response.data.success && response.data.rows) {
        const rankingsData = response.data.rows.map((player, index) => ({
          name: player.player,
          score: parseInt(player.score),
          time_formatted: player.time_formatted,
          created_at: player.created_at,
          rank: index + 1
        }));
        
        setRankings(rankingsData);
        
        // Find current user's rank
        const userRank = rankingsData.find(player => 
          player.name === props.login?.first_name ||
          player.name.includes(props.login?.first_name) ||
          player.name.includes(props.login?.last_name)
        );
        setCurrentUserRank(userRank);
      } else {
        console.error('Invalid API response:', response.data);
        setRankings([]);
      }
      
    } catch (error) {
      console.error('Error fetching rankings:', error);
      setRankings([]);
    } finally {
      setLoadingRankings(false);
    }
  };

  const getRankBadgeColor = (rank) => {
    switch(rank) {
      case 1: 
        return {
          background: 'linear-gradient(to right, #fbbf24, #d97706)',
          color: '#78350f'
        };
      case 2: 
        return {
          background: 'linear-gradient(to right, #d1d5db, #6b7280)',
          color: '#111827'
        };
      case 3: 
        return {
          background: 'linear-gradient(to right, #d97706, #92400e)',
          color: '#fef3c7'
        };
      default: 
        return {
          background: 'linear-gradient(to right, #3b82f6, #2563eb)',
          color: 'white'
        };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    height: '100%'
  };

  const personalResultsStyle = {
    textAlign: 'center',
    marginBottom: '24px'
  };

  const pointsStyle = {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: '8px'
  };

  const timeStyle = {
    fontSize: '14px',
    color: '#666'
  };

  const currentRankStyle = {
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#e3f2fd',
    borderRadius: '8px',
    border: '1px solid #bbdefb'
  };

  const currentRankTextStyle = {
    fontSize: '14px',
    color: '#1565c0'
  };

  const leaderboardContainerStyle = {
    width: '100%',
    maxWidth: '512px'
  };

  const leaderboardHeaderStyle = {
    textAlign: 'center',
    marginBottom: '16px'
  };

  const leaderboardHeaderContentStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px'
  };

  const refreshButtonStyle = {
    padding: '8px 12px',
    border: '2px solid #1976d2',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    color: '#1976d2',
    fontWeight: '500',
    cursor: loadingRankings ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
    justifyContent: 'center',
    width: 120
  };

  const restartButtonStyle = {
    padding: '8px 12px',
    border: '2px solid #1976d2',
    borderRadius: '6px',
    fontWeight: '500',
    backgroundColor: 'transparent',
    color: loading ? '#ccc' : '#1976d2',
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    width: 120
  };

  const trophyIconContainerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    background: 'linear-gradient(to right, #7c3aed, #4f46e5)',
    borderRadius: '50%',
    marginBottom: '8px'
  };

  const leaderboardTitleStyle = {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1976d2',
    margin: 0
  };

  const loadingStyle = {
    textAlign: 'center',
    padding: '32px 0',
    color: '#666'
  };

  const rankingsListStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '256px',
    overflowY: 'auto'
  };

  const getPlayerCardStyle = (isCurrentUser) => ({
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: isCurrentUser ? '1px solid #90caf9' : '1px solid #e0e0e0',
    backgroundColor: isCurrentUser ? '#e3f2fd' : 'white',
    padding: '16px'
  });

  const playerCardContentStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const playerLeftSectionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const getRankBadgeStyle = (rank) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    fontWeight: 'bold',
    fontSize: '14px',
    ...getRankBadgeColor(rank)
  });

  const getPlayerNameStyle = (isCurrentUser) => ({
    fontWeight: '600',
    color: isCurrentUser ? '#1565c0' : '#333',
    margin: 0
  });

  const youBadgeStyle = {
    marginLeft: '8px',
    fontSize: '12px',
    backgroundColor: '#bbdefb',
    color: '#1565c0',
    padding: '2px 8px',
    borderRadius: '4px'
  };

  const playerRightSectionStyle = {
    textAlign: 'right'
  };

  const scoreStyle = {
    fontWeight: 'bold',
    fontSize: '18px',
    color: '#333',
    margin: 0
  };

  const timeContainerStyle = {
    fontSize: '12px',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    justifyContent: 'flex-end'
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* Personal Results */}
      <div style={personalResultsStyle}>
        <div style={{marginBottom: '16px'}}>
          Thanks for playing, {props.login?.first_name}!
        </div>
        
        <div style={{marginBottom: '16px'}}>
          <div style={pointsStyle}>
            {points} points
          </div>
          <div style={timeStyle}>
            Completed in {formatTime(timer)}
          </div>
        </div>

        {currentUserRank && (
          <div style={currentRankStyle}>
            <div style={currentRankTextStyle}>
              Your current ranking: <span style={{fontWeight: 'bold'}}>#{currentUserRank.rank}</span>
            </div>
          </div>
        )}
      </div>

      {/* Leaderboard */}
      <div style={leaderboardContainerStyle}>
        <div style={leaderboardHeaderStyle}>
          <div style={trophyIconContainerStyle}>
            <FontAwesomeIcon icon={faTrophy} style={{color: 'white', fontSize: '24px'}} />
          </div>
          <div style={leaderboardHeaderContentStyle}>
            <h3 style={leaderboardTitleStyle}>
              Top Performers
            </h3>
          </div>
        </div>

        {loadingRankings ? (
          <div style={loadingStyle}>
            <div>Loading rankings...</div>
          </div>
        ) : (
          <div style={rankingsListStyle}>
            {rankings.slice(0, 10).map((player, index) => {
              const isCurrentUser = player.name === props.login?.first_name;
              return (
                <div 
                  key={index}
                  style={getPlayerCardStyle(isCurrentUser)}
                >
                  <div style={playerCardContentStyle}>
                    {/* Rank and Name */}
                    <div style={playerLeftSectionStyle}>
                      <div style={getRankBadgeStyle(player.rank)}>
                        #{player.rank}
                      </div>
                      <div>
                        <div style={getPlayerNameStyle(isCurrentUser)}>
                          {player.name}
                          {isCurrentUser && (
                            <span style={youBadgeStyle}>You</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Score and Time */}
                    <div style={playerRightSectionStyle}>
                      <div style={scoreStyle}>
                        {player.score}
                      </div>
                      <div style={timeContainerStyle}>
                        <FontAwesomeIcon icon={faClock} style={{fontSize: '12px'}} />
                        {player.time_formatted}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className='rowContainerBetween' style={{marginTop: '16px'}}>
            <button
              style={refreshButtonStyle}
              onClick={fetchRankings}
              disabled={loadingRankings}
              onMouseEnter={(e) => {
                if (!loadingRankings) {
                  e.target.style.backgroundColor = '#1976d2';
                  e.target.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (!loadingRankings) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#1976d2';
                }
              }}
            >
              <FontAwesomeIcon 
                icon={faSync} 
                style={{
                  fontSize: '12px',
                  animation: loadingRankings ? 'spin 1s linear infinite' : 'none'
                }} 
              />
              {loadingRankings ? 'Refreshing...' : ''}
            </button>
            <button 
              disabled={loading} 
              style={restartButtonStyle}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#1976d2';
                  e.target.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#1976d2';
                }
              }}
              onClick={onRestart}
            >
              RESTART
            </button>
        </div>
      </div>
    </div>
  );
};

export default QuizResultsLeaderboard;